import axios from "axios";

// Centrale API-basis (voorstel 51). Ontbreekt de build-variabele (bv. frontend/.env niet in de image) → zelfde oorsprong,
// dus relatief "/api" en nooit "undefined/api". Alle bestanden gebruiken deze constante, nooit process.env rechtstreeks.
const _ruw = process.env.REACT_APP_BACKEND_URL;
export const BACKEND_URL = typeof _ruw === "string" && _ruw && _ruw !== "undefined" ? _ruw.replace(/\/+$/, "") : "";
export const API = `${BACKEND_URL}/api`;

/** Altijd een array (API-antwoord kan object met items/lijst zijn, of iets onverwachts) — voorkomt "x.filter is not a function". */
export function lijst(x, sleutel) {
  if (Array.isArray(x)) return x;
  if (x && typeof x === "object") {
    if (sleutel && Array.isArray(x[sleutel])) return x[sleutel];
    for (const k of ["items", "lijst", "results", "data"]) if (Array.isArray(x[k])) return x[k];
  }
  return [];
}

const _isHtml = (d) => typeof d === "string" && /^\s*<(!doctype|html)/i.test(d);

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Centrale rechtenbewaking: 403 "Alleen-lezen" → één duidelijke melding (pagina's tonen vaak alleen een generieke fout)
let _laatsteRechtenToast = 0;
const _autoGemeld = new Set();
function _autoMelding(error) {
  try {
    const st = error?.response?.status;
    if (!st || st < 500 || !window.__bptFlags?.meldknop_v1) return;
    const pad = (error.config?.url || "").split("?")[0];
    const sleutel = `${st}:${pad}`;
    if (_autoGemeld.has(sleutel) || pad.includes("/meldknop")) return;
    _autoGemeld.add(sleutel);
    api.post("/meldknop/auto", { soort: "fout", titel: `Serverfout ${st} op ${pad}`, detail: JSON.stringify(error.response?.data || {}).slice(0, 1500), context: { route: window.location.pathname, methode: error.config?.method, tab: new URLSearchParams(window.location.search).get("tab") } }).catch(() => {});
  } catch { /* stil */ }
}

api.interceptors.response.use((r) => {
  if (_isHtml(r.data)) {  // app-HTML i.p.v. JSON = verkeerde API-basis of SPA-fallback → duidelijke fout, geen stille crash
    const pad = (r.config?.url || "").split("?")[0];
    const e = new Error(`API gaf HTML terug in plaats van JSON (${pad}) — controleer de API-basis (REACT_APP_BACKEND_URL)`);
    e.config = r.config; e.response = { status: 502, data: { detail: e.message }, headers: r.headers };
    if (!window.__bptHtmlGemeld) { window.__bptHtmlGemeld = true; console.error("[BPTplus] " + e.message, { basis: API }); }
    return Promise.reject(e);
  }
  return r;
}, (error) => {
  _autoMelding(error);
  const d = error?.response?.data;
  if (error?.response?.status === 403 && (error.config?.method || "get").toLowerCase() === "get" && !error.config?.bptStil) {
    // Paginalading zonder recht → Layout toont één duidelijke "Geen toegang"-balk i.p.v. een lege pagina (crawl 19-10)
    try { window.dispatchEvent(new CustomEvent("bpt:geen-toegang", { detail: { pad: (error.config?.url || "").split("?")[0], reden: typeof d?.detail === "string" ? d.detail : "" } })); } catch { /* stil */ }
  }
  if (error?.response?.status === 403 && d?.rechten && Date.now() - _laatsteRechtenToast > 1500) {
    _laatsteRechtenToast = Date.now();
    import("sonner").then(({ toast }) => toast.error(d.detail, { id: "rechten-403", duration: 6000 })).catch(() => {});
  }
  return Promise.reject(error);
});

export function formatErr(detail) {
  if (detail == null) return "Er ging iets mis. Probeer het opnieuw.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Kortlevend sessietoken voor document-links (pdl_, voorstel 31 p3): nooit het inlogtoken in een URL.
// Eén token per sessie, 15 min geldig, wordt elke 5 min verlengd zolang de app open staat.
const DL_DUUR = 900;
let _dlToken = sessionStorage.getItem("bpt_dl_token") || null;
let _dlTimer = null;

export async function refreshDownloadToken() {
  if (!localStorage.getItem("token")) return null;
  try {
    const { data } = await api.post("/pdf-token", { duur: DL_DUUR, verleng: _dlToken || undefined });
    _dlToken = data.token;
    sessionStorage.setItem("bpt_dl_token", _dlToken);
  } catch { /* volgende poging bij de timer */ }
  return _dlToken;
}

export function startDownloadTokenRefresh() {
  if (_dlTimer) return;
  _dlTimer = setInterval(refreshDownloadToken, 5 * 60 * 1000);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") refreshDownloadToken(); });
}

export function stopDownloadToken() {
  _dlToken = null;
  sessionStorage.removeItem("bpt_dl_token");
}

/** Query-parameter voor links die buiten axios om worden geopend (PDF/CSV/afbeeldingen): `auth=pdl_…` */
export function authQuery() {
  return `auth=${encodeURIComponent(_dlToken || "")}`;
}

export function downloadUrl(fileId) {
  return `${API}/files/${fileId}/download?${authQuery()}`;
}
